import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './TermsScreen.css';

const TermsScreen = () => {
  return (
    <div className="terms-page">
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="modern-card">
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  <h1 className="terms-title">Terms of Service</h1>
                  <p className="terms-subtitle text-secondary">
                    Last updated: 8/1/2026
                  </p>
                </div>

                <div className="terms-content">
                  <section className="terms-section">
                    <h2>1. Acceptance of Terms</h2>
                    <p>
                      By accessing and using League Runner ("the Service"), you accept and agree to be 
                      bound by the terms and provision of this agreement. If you do not agree to these 
                      Terms of Service, you should not use the Service.
                    </p>
                  </section>

                  <section className="terms-section">
                    <h2>2. Description of Service</h2>
                    <p>
                      League Runner is a web-based platform that provides running league management 
                      services, including but not limited to:
                    </p>
                    <ul>
                      <li>User account creation and management</li>
                      <li>Running league organization and participation</li>
                      <li>Team formation and management</li>
                      <li>Draft processes for athlete selection</li>
                      <li>Performance tracking and statistics</li>
                      <li>Integration with third-party fitness platforms (Strava)</li>
                      <li>Optional ad-free subscriptions and one-time purchases</li>
                    </ul>
                  </section>

                  <section className="terms-section">
                    <h2>3. User Accounts and Registration</h2>
                    <h3>Account Requirements</h3>
                    <ul>
                      <li>You must provide accurate and complete information during registration</li>
                      <li>You are responsible for maintaining the security of your account</li>
                      <li>You must be at least 13 years old to create an account</li>
                      <li>One person may not maintain multiple accounts</li>
                    </ul>
                    
                    <h3>Account Responsibilities</h3>
                    <ul>
                      <li>You are responsible for all activities that occur under your account</li>
                      <li>You must notify us immediately of any unauthorized use of your account</li>
                      <li>You agree to keep your login credentials confidential</li>
                    </ul>
                  </section>

                  <section className="terms-section">
                    <h2>4. Acceptable Use Policy</h2>
                    <h3>Permitted Uses</h3>
                    <ul>
                      <li>Participate in running leagues and competitions</li>
                      <li>Track and share your running performance</li>
                      <li>Interact with other users in a respectful manner</li>
                      <li>Use the platform for its intended recreational and competitive purposes</li>
                    </ul>

                    <h3>Prohibited Uses</h3>
                    <p>You agree not to:</p>
                    <ul>
                      <li>Use the Service for any unlawful purpose</li>
                      <li>Impersonate other users or provide false information</li>
                      <li>Interfere with or disrupt the Service or servers</li>
                      <li>Attempt to gain unauthorized access to any part of the Service</li>
                      <li>Upload malicious code or attempt to harm the platform</li>
                      <li>Harass, abuse, or threaten other users</li>
                      <li>Share inappropriate or offensive content</li>
                      <li>Falsify running data or performance metrics</li>
                    </ul>
                  </section>

                  <section className="terms-section">
                    <h2>5. Data and Privacy</h2>
                    <h3>Your Data</h3>
                    <ul>
                      <li>You retain ownership of your personal running data and activity information</li>
                      <li>You grant us permission to use your data to provide the Service</li>
                      <li>Your data may be visible to other league and team members as part of the competitive experience</li>
                    </ul>

                    <h3>Strava Integration</h3>
                    <ul>
                      <li>Connecting your Strava account is optional</li>
                      <li>You control what data is imported from Strava</li>
                      <li>You can disconnect your Strava account at any time</li>
                      <li>Strava data usage is subject to Strava's own terms and policies</li>
                    </ul>
                  </section>

                  <section className="terms-section">
                    <h2>6. League and Competition Rules</h2>
                    <h3>Fair Play</h3>
                    <ul>
                      <li>All running data must be accurate and truthful</li>
                      <li>Manipulating or falsifying performance data is prohibited</li>
                      <li>Unsportsmanlike conduct may result in account suspension</li>
                    </ul>

                    <h3>League Administration</h3>
                    <ul>
                      <li>League administrators have the authority to manage their leagues</li>
                      <li>Draft rules and scoring systems are set by league administrators</li>
                      <li>Disputes should be resolved through league administration first</li>
                    </ul>
                  </section>

                  <section className="terms-section">
                    <h2>7. Intellectual Property</h2>
                    <h3>Our Rights</h3>
                    <ul>
                      <li>League Runner owns all rights to the platform, design, and functionality</li>
                      <li>Our trademarks and logos are protected intellectual property</li>
                      <li>You may not copy, modify, or redistribute our software or content</li>
                    </ul>

                    <h3>User Content</h3>
                    <ul>
                      <li>You retain rights to content you create or upload</li>
                      <li>You grant us a license to use your content to provide the Service</li>
                      <li>You are responsible for ensuring you have rights to any content you share</li>
                    </ul>
                  </section>

                  <section className="terms-section">
                    <h2>8. Service Availability and Modifications</h2>
                    <h3>Service Availability</h3>
                    <ul>
                      <li>We strive to maintain high availability but cannot guarantee 100% uptime</li>
                      <li>Scheduled maintenance may temporarily interrupt service</li>
                      <li>We are not liable for losses due to service interruptions</li>
                    </ul>

                    <h3>Service Modifications</h3>
                    <ul>
                      <li>We may modify or discontinue features at any time</li>
                      <li>We will provide reasonable notice for significant changes</li>
                      <li>Continued use of the Service constitutes acceptance of modifications</li>
                    </ul>
                  </section>

                  <section className="terms-section">
                    <h2>9. Billing, Renewals, and Cancellations</h2>
                    <ul>
                      <li>Subscriptions and one-time purchases are processed by Stripe, our payment processor.</li>
                      <li>Subscription plans renew automatically until you cancel them through the Stripe billing portal.</li>
                      <li>When you cancel a subscription, your paid access remains active through the end of the current billing period.</li>
                      <li>Lifetime ad-free purchases are one-time charges and do not renew.</li>
                      <li>Refund requests, billing questions, and charge issues should be sent to ethan@leaguerunner.net.</li>
                    </ul>
                  </section>

                  <section className="terms-section">
                    <h2>10. Disclaimers and Limitation of Liability</h2>
                    <h3>Disclaimers</h3>
                    <ul>
                      <li>The Service is provided "as is" without warranties</li>
                      <li>We do not guarantee the accuracy of user-provided data</li>
                      <li>Running activities carry inherent risks - participate responsibly</li>
                      <li>We are not responsible for injuries resulting from running activities</li>
                    </ul>

                    <h3>Limitation of Liability</h3>
                    <p>
                      To the fullest extent permitted by law, League Runner shall not be liable for any 
                      indirect, incidental, special, consequential, or punitive damages, including but not 
                      limited to loss of profits, data, or use, arising from your use of the Service.
                    </p>
                  </section>

                  <section className="terms-section">
                    <h2>11. Termination</h2>
                    <h3>Termination by You</h3>
                    <ul>
                      <li>You may delete your account at any time</li>
                      <li>Account deletion will remove your personal data</li>
                      <li>Some aggregated data may remain for statistical purposes</li>
                    </ul>

                    <h3>Termination by Us</h3>
                    <ul>
                      <li>We may terminate accounts for violations of these terms</li>
                      <li>We may suspend accounts pending investigation of violations</li>
                      <li>We will provide notice when reasonably possible</li>
                    </ul>
                  </section>

                  <section className="terms-section">
                    <h2>12. Changes to Terms</h2>
                    <p>
                      We reserve the right to modify these Terms of Service at any time. We will notify 
                      users of material changes through:
                    </p>
                    <ul>
                      <li>Email notification to registered users</li>
                      <li>Prominent notice on the platform</li>
                      <li>Update to the "Last updated" date</li>
                    </ul>
                    <p>
                      Continued use of the Service after changes constitutes acceptance of the new terms.
                    </p>
                  </section>

                  <section className="terms-section">
                    <h2>13. Contact Information</h2>
                    <p>
                      For questions about these Terms of Service, please contact us at:
                    </p>
                    <div className="contact-info">
                      <p><strong>Email:</strong> ethan@leaguerunner.net</p>
                    </div>
                  </section>

                  <div className="terms-footer">
                    <hr className="my-5" />
                    <div className="text-center">
                      <Link to="/" className="btn btn-primary me-3">
                        Back to Home
                      </Link>
                      <Link to="/privacy" className="btn btn-outline-secondary">
                        Privacy Policy
                      </Link>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TermsScreen;
